import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { personalizationService } from '@/services/personalizationService'
import { toast } from 'vue3-toastify'

export const usePersonalizationStore = defineStore('personalization', () => {
  // State
  const form = ref(null)
  const formFields = ref([])
  const answers = ref({})
  const loading = ref(false)
  const submitting = ref(false)
  const status = ref({
    completed: false,
    personalization_form_completed: false,
    counselor_selected: false,
    completed_at: null,
    counselor_selected_at: null,
    required: false
  })
  const myData = ref(null)
  const konselors = ref([])

  // Computed
  const isCompleted = computed(() => status.value.completed)
  const isRequired = computed(() => status.value.required)
  const formFieldsSorted = computed(() => {
    return [...formFields.value].sort((a, b) => a.order_index - b.order_index)
  })

  // Actions
  async function fetchForm() {
    try {
      loading.value = true
      const response = await personalizationService.getForm()
      form.value = response
      formFields.value = response.form_fields || []

      // Initialize answers object with proper default values based on field type
      answers.value = {}
      formFields.value.forEach(field => {
        if (field.field_type === 'multiselect') {
          answers.value[field.id] = []
        } else if (field.field_type === 'select') {
          answers.value[field.id] = ''
        } else {
          answers.value[field.id] = null
        }
      })

      return response
    } catch (error) {
      console.error('Failed to fetch personalization form:', error)
      toast.error('Gagal memuat form personalisasi')
      throw error
    } finally {
      loading.value = false
    }
  }

  async function submitPersonalization(formAnswers) {
    try {
      submitting.value = true

      // Transform answers to backend format
      const answersArray = Object.entries(formAnswers).map(([fieldId, value]) => ({
        field_id: fieldId,
        value: value
      }))

      const response = await personalizationService.submitForm({
        answers: answersArray
      })

      // Update status
      status.value.completed = true
      status.value.completed_at = new Date().toISOString()

      toast.success('Personalisasi berhasil disimpan!')
      return response
    } catch (error) {
      console.error('Failed to submit personalization:', error)
      const errorMessage = error.response?.data?.message || 'Gagal menyimpan personalisasi'
      toast.error(errorMessage)
      throw error
    } finally {
      submitting.value = false
    }
  }

  async function checkStatus() {
    try {
      const response = await personalizationService.checkStatus()
      status.value = {
        completed: response.completed || false,
        personalization_form_completed: response.personalization_form_completed || false,
        counselor_selected: response.counselor_selected || false,
        completed_at: response.completed_at || null,
        counselor_selected_at: response.counselor_selected_at || null,
        required: response.required || false
      }
      return response
    } catch (error) {
      console.error('Failed to check personalization status:', error)
      // Return default status instead of throwing to avoid blocking navigation
      status.value = {
        completed: false,
        personalization_form_completed: false,
        counselor_selected: false,
        completed_at: null,
        counselor_selected_at: null,
        required: false
      }
      return status.value
    }
  }

  async function fetchMyPersonalization() {
    try {
      loading.value = true
      const response = await personalizationService.getMyPersonalization()
      myData.value = response
      return response
    } catch (error) {
      console.error('Failed to fetch personalization data:', error)
      toast.error('Gagal memuat data personalisasi')
      throw error
    } finally {
      loading.value = false
    }
  }

  async function fetchKonselors() {
    try {
      const response = await personalizationService.getKonselors()
      konselors.value = response.data || []
      return response
    } catch (error) {
      console.error('Failed to fetch konselors:', error)
      toast.error('Gagal memuat daftar konselor')
      throw error
    }
  }

  async function updateUserPersonalData(userId, data) {
    try {
      const response = await personalizationService.updatePersonalData(userId, data)
      toast.success('Data personal berhasil diupdate')
      return response
    } catch (error) {
      console.error('Failed to update personal data:', error)
      const errorMessage = error.response?.data?.message || 'Gagal mengupdate data personal'
      toast.error(errorMessage)
      throw error
    }
  }

  async function assignKonselorToMahasiswa(mahasiswaId, konselorId) {
    try {
      const response = await personalizationService.assignKonselor(mahasiswaId, konselorId)
      toast.success('Konselor berhasil ditugaskan')
      return response
    } catch (error) {
      console.error('Failed to assign konselor:', error)
      const errorMessage = error.response?.data?.message || 'Gagal menugaskan konselor'
      toast.error(errorMessage)
      throw error
    }
  }

  function resetForm() {
    answers.value = {}
    formFields.value.forEach(field => {
      answers.value[field.id] = null
    })
  }

  function $reset() {
    form.value = null
    formFields.value = []
    answers.value = {}
    loading.value = false
    submitting.value = false
    status.value = {
      completed: false,
      completed_at: null,
      required: false
    }
    myData.value = null
    konselors.value = []
  }

  return {
    // State
    form,
    formFields,
    formFieldsSorted,
    answers,
    loading,
    submitting,
    status,
    myData,
    konselors,

    // Computed
    isCompleted,
    isRequired,

    // Actions
    fetchForm,
    submitPersonalization,
    checkStatus,
    fetchMyPersonalization,
    fetchKonselors,
    updateUserPersonalData,
    assignKonselorToMahasiswa,
    resetForm,
    $reset
  }
})
